import { aircraftQuery } from "@common/api/aircraft"
import { featureFlagsQuery, useFeatureFlag } from "@common/api/feature-flags"
import { Button } from "@common/components/ui/button"
import { DASHBOARD_AIRCRAFT } from "@common/flags"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
} from "@tanstack/react-router"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DisplayOptions } from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage } from "@/components/layout/list-page"
import { ListTabs } from "@/components/layout/list-tabs"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requirePermission } from "@/lib/require-permission"
import { AircraftTable } from "./-components/aircraft-table"
import { FleetOverview } from "./-components/fleet-overview"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  DEFAULT_COLUMNS,
} from "./-lib/columns"
import {
  type AircraftFilterValues,
  aircraftFilterConfigs,
  hasAircraftFilters,
  NO_AIRCRAFT_FILTERS,
} from "./-lib/filters"
import { GROUPING_LABELS, GROUPINGS } from "./-lib/grouping"
import {
  type AircraftDisplay,
  type AircraftSearch,
  aircraftSearchSchema,
  displayOf,
  FLEET,
  LIST_TABS,
  type ListTab,
  listTabLabel,
} from "./-lib/search"

export const Route = createFileRoute("/_layout/aircraft")({
  staticData: { breadcrumb: "Aircraft" },
  validateSearch: (search) => aircraftSearchSchema.parse(search),
  beforeLoad: ({ context: { queryClient } }) =>
    requirePermission(queryClient, "aircraft:read", "/"),
  loader: ({ context: { queryClient } }) => {
    void queryClient.prefetchQuery(featureFlagsQuery())
    return queryClient.ensureQueryData(aircraftQuery(FLEET))
  },
  component: AircraftPage,
})

const TABLE_SEARCH: Partial<AircraftSearch> = {
  tab: undefined,
  months: undefined,
  end: undefined,
  source: undefined,
}

function useUpdateSearch(): (patch: Partial<AircraftSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function AircraftPage(): React.ReactElement {
  const search = Route.useSearch()
  const display = displayOf(search)
  const update = useUpdateSearch()
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)
  const tabbed = useFeatureFlag(DASHBOARD_AIRCRAFT) ?? false
  const tab = tabbed ? (search.tab ?? "all") : "all"

  return (
    <ListPage
      toolbar={
        <Toolbar
          tab={tab}
          tabbed={tabbed}
          display={display}
          hidden={fit.hidden}
        />
      }
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilters />
      {tab === "overview" ? (
        <FleetOverview />
      ) : (
        <AircraftTable
          search={search}
          display={display}
          columns={fit.shown}
          onSort={(column) => update(sortSearch(display, column))}
          onClear={() => update({ ...NO_AIRCRAFT_FILTERS, search: undefined })}
        />
      )}
    </ListPage>
  )
}

function Toolbar({
  tab,
  tabbed,
  display,
  hidden,
}: {
  tab: ListTab
  tabbed: boolean
  display: AircraftDisplay
  hidden: Column[]
}): React.ReactElement {
  const values = Route.useSearch()
  const update = useUpdateSearch()
  const canCreate = useHasPermission("aircraft:create")
  const tabs = tabbed && (
    <ListTabs
      tabs={LIST_TABS}
      tab={tab}
      label={listTabLabel}
      onSelect={(next) => update(next === "all" ? TABLE_SEARCH : { tab: next })}
    />
  )

  if (tab === "overview") {
    return (
      <ListToolbar
        tabs={tabs}
        actions={
          <FilterButton
            active={hasAircraftFilters(values)}
            configs={aircraftFilterConfigs}
            onChange={update}
          />
        }
      />
    )
  }

  return (
    <ListToolbar
      tabs={tabs}
      actions={
        <>
          <NameFilter
            search={values.search ?? ""}
            onSearch={(next) => update({ search: next || undefined })}
            placeholder="Search aircraft…"
          />
          <FilterButton
            active={hasAircraftFilters(values)}
            configs={aircraftFilterConfigs}
            onChange={update}
          />
          <DisplayOptions
            groupings={GROUPINGS}
            labels={GROUPING_LABELS}
            group={display.group}
            subgroup={display.subgroup}
            onChange={(next) =>
              update({ group: next.group, subgroup: next.subgroup })
            }
          >
            <ColumnChips
              options={COLUMNS}
              labels={COLUMN_LABELS}
              fixed={["registration"]}
              columns={display.columns}
              hidden={hidden}
              onChange={(next) => update(columnsSearch(next))}
            />
          </DisplayOptions>
          {canCreate && (
            <Button asChild size="sm">
              <Link to="/aircraft/new" search={true}>
                New aircraft
              </Link>
            </Button>
          )}
        </>
      }
    />
  )
}

function columnsSearch(columns: Column[]): Partial<AircraftSearch> {
  const defaults =
    columns.length === DEFAULT_COLUMNS.length &&
    columns.every((column) => DEFAULT_COLUMNS.includes(column))
  return { columns: defaults ? undefined : columns }
}

function sortSearch(
  display: AircraftDisplay,
  column: Column,
): Partial<AircraftSearch> {
  const direction =
    column === display.sort && display.direction === "asc" ? "desc" : "asc"
  const byDefault = column === "registration" && direction === "asc"
  return byDefault
    ? { sort: undefined, direction: undefined }
    : { sort: column, direction }
}

function ActiveFilters(): React.ReactElement {
  const { manufacturer, model, updater } = Route.useSearch()
  const update = useUpdateSearch()
  const values: AircraftFilterValues = { manufacturer, model, updater }

  return (
    <ActiveFilterBar
      active={hasAircraftFilters(values)}
      configs={aircraftFilterConfigs}
      values={values}
      onChange={update}
      onClear={() => update(NO_AIRCRAFT_FILTERS)}
    />
  )
}
